// Recurring BCM activities.
//
// HOW TO MAINTAIN THIS FILE
// Set `visible: true` only after BCM leadership confirms the day, time, and
// location. Anything left as a draft stays out of the rendered page entirely —
// the public components read `visibleRecurring`, never this raw array — so no
// tentative schedule can leak into cards, labels, tooltips, or screen readers.

export type RecurringActivity = {
  id: string
  activity: string
  /** Weekday, e.g. "Thursday". Only rendered when visible. */
  day: string
  /** Time, e.g. "7:30 PM". Only rendered when visible. */
  time: string
  location: string
  /** Fuller location sentence used in the Typical Week card. */
  locationDetail?: string
  description: string
  /** Internal maintenance status. Never rendered. */
  status: string
  /** Gate for public display. */
  visible: boolean
  /** Where students should look for the latest details. */
  updateLink?: string
}

import { externalLinks } from './site'

const recurringActivities: RecurringActivity[] = [
  {
    id: 'thursday-worship',
    activity: 'Thursday Worship Nights',
    day: 'Thursday',
    time: '7:30 PM',
    location: 'Most weeks: Cape Florida Ballroom, Room 316',
    locationDetail:
      'Most weeks: Cape Florida Ballroom, Room 316, on the third floor of the UCF Student Union.',
    description:
      'Gather for worship, a Bible-centered message, fellowship, and time to meet other students.',
    status: 'Confirmed general schedule',
    visible: true,
    updateLink: externalLinks.instagram,
  },
  {
    id: 'womens-community-group',
    activity: 'Women’s Community Group',
    day: 'Monday',
    time: 'Possible 7:00 PM',
    location: 'Not confirmed',
    description: 'Scripture, conversation, friendship, and prayer.',
    status: 'Draft—confirm with BCM leadership',
    visible: false,
    updateLink: externalLinks.instagram,
  },
  {
    id: 'mens-community-group',
    activity: 'Men’s Community Group',
    day: 'Monday',
    time: 'Possible 7:00 PM',
    location: 'Not confirmed',
    description: 'Scripture, conversation, friendship, and prayer.',
    status: 'Draft—confirm with BCM leadership',
    visible: false,
    updateLink: externalLinks.instagram,
  },
  {
    id: 'volleyball',
    activity: 'Volleyball',
    day: 'Friday',
    time: 'Possible 7:00 PM',
    location: 'Possible Lake Claire Recreational Area',
    description: 'Recreational volleyball with BCM students.',
    status: 'Draft—confirm with BCM leadership',
    visible: false,
    updateLink: externalLinks.instagram,
  },
]

/** The only recurring records any public component may render. */
export const visibleRecurring: RecurringActivity[] = recurringActivities.filter(
  (activity) => activity.visible,
)

/**
 * Cards for the "A typical week at BCM" section. Each card either renders a
 * confirmed schedule (pulled from `visibleRecurring`) or a neutral status line
 * while details are still being confirmed.
 */
export type TypicalWeekCard = {
  id: string
  label: string
  title: string
  /** Copy shown for cards without a confirmed schedule. */
  copy?: string
  /** Set only when a confirmed recurring record backs this card. */
  recurringId?: string
  clarification?: string
  /** Neutral status shown instead of a day/time while unconfirmed. */
  scheduleStatus?: string
  cta?: { label: string; to: string }
}

export const typicalWeekCards: TypicalWeekCard[] = [
  {
    id: 'weekly-worship',
    label: 'Weekly Worship',
    title: 'Thursday Worship Nights',
    recurringId: 'thursday-worship',
    clarification: 'Check the Events page or Instagram for that week’s confirmed location.',
    cta: { label: 'Plan Your First Thursday', to: '/#first-thursday' },
  },
  {
    id: 'community-groups',
    label: 'Grow Together',
    title: 'Community Groups',
    copy: 'Women’s and men’s community groups meet during the semester for Scripture, conversation, friendship, and prayer.',
    scheduleStatus: 'Fall schedule coming soon',
  },
  {
    id: 'recreation',
    label: 'Community & Recreation',
    title: 'Volleyball, Social Nights & Special Events',
    copy: 'BCM gathers throughout the semester for recreation, outreach, meals, trips, and other opportunities to build community.',
    scheduleStatus: 'Weekly details are announced online',
  },
]

/** Lookup used by the card component; returns undefined for unconfirmed cards. */
export function findVisibleRecurring(id?: string) {
  if (!id) return undefined
  return visibleRecurring.find((activity) => activity.id === id)
}
